/**
 * DependencyDependentList — small "Used by" / "References" sub-list rendered
 * inside `DependencySidebar`. Extracted into a sibling file to keep
 * `DependencySidebar.tsx` under the 200-line CLAUDE.md budget.
 */
import type { DependencyItem } from '../../dependencies/types';

const REPRESENTATIVE_LIMIT = 3;

export interface DependencyDependentListProps {
  readonly heading: string;
  readonly items: readonly DependencyItem[];
  readonly testId: string;
}

export function DependencyDependentList({
  heading,
  items,
  testId,
}: DependencyDependentListProps) {
  const visible = items.slice(0, REPRESENTATIVE_LIMIT);
  const overflow = items.length - visible.length;
  return (
    <div className="mt-1 flex flex-col gap-0.5" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {heading}
      </span>
      <ul className="flex flex-col gap-0.5">
        {visible.map((item) => {
          const fullName = item.schema ? `${item.schema}.${item.name}` : item.name;
          return (
            <li
              key={`${item.schema}.${item.name}.${item.objectType}`}
              className="flex min-w-0 items-baseline gap-1 font-mono text-[11px] text-foreground"
              title={fullName}
              data-testid={`${testId}-item`}
              data-object-type={item.objectType}
              data-via-loose={item.viaLooseRelationship ? 'true' : undefined}
            >
              <span className="truncate">{fullName}</span>
              <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                {item.objectType}
              </span>
            </li>
          );
        })}
      </ul>
      {overflow > 0 ? (
        <span
          className="text-[10px] italic text-muted-foreground"
          data-testid={`${testId}-overflow`}
        >
          +{overflow} more
        </span>
      ) : null}
    </div>
  );
}
